import type { DataProvider } from "ra-core";

// GATE_SORT_BASE lives with the track rules because it is a property of
// how a track is laid out, not of one stage. No cycle: that module
// imports only from ./types.
import { GATE_SORT_BASE } from "./lifecycleTrackRules";
import type { Invalidator } from "./touchActions";
import type { ContactHealth, LifecycleStage, LifecycleTrack } from "./types";

/**
 * The rules behind the lifecycle-stage settings screen.
 *
 * WHAT THIS FILE IS FOR
 * ---------------------
 * `public.lifecycle_stages` exists so that "changing a staleness threshold is
 * data, not a migration" -- that sentence is the table's own comment in the
 * database. Until this screen there was no way to change it except by hand in
 * psql, so the one thing the table was built for was the one thing nobody
 * could do.
 *
 * The logic lives here rather than in LifecycleStages.tsx for the reason the
 * directory's CLAUDE.md gives: every refusal below is provable in Node in
 * about a second, and a mutation check that costs a second gets run.
 *
 * WHAT THE DATABASE ENFORCES, AND WHAT IT DOES NOT
 * ------------------------------------------------
 * Read from the live catalog on 2026-09-04, not from a migration file:
 *
 *   lifecycle_stages_terminal_has_no_cadence
 *       CHECK (NOT is_terminal OR stale_after_days IS NULL)
 *   lifecycle_stages_track_check
 *       CHECK (track = ANY (ARRAY['prospect','partner']))
 *   lifecycle_stages_pkey  PRIMARY KEY (stage, track)
 *
 * and nothing else. Three consequences drive most of this file:
 *
 * 1. THE TERMINAL RULE IS ONE-DIRECTIONAL. The check constrains terminal rows
 *    only. A NON-terminal stage with a null cadence is perfectly acceptable to
 *    the table -- and contact_health's
 *        coalesce(cl.stale_after_days, s.stale_after_days, 7)
 *    then falls through to the literal 7. So clearing the partner cadence does
 *    not switch the clock off; it silently moves every partner from 30 days to
 *    7. That refusal has to live here, because nothing below it will object.
 *
 * 2. THERE IS NO `stale_after_days > 0` CHECK ON THIS TABLE. `contact_lifecycle`
 *    has one (`contact_lifecycle_stale_after_days_check`); lifecycle_stages
 *    does not. A cadence of 0 saves cleanly and makes `now > due_at` true for
 *    ever, for everyone on the stage.
 *
 * 3. THERE IS NO UNIQUE INDEX ON (track, sort). The primary key is the only
 *    index on the table. Two stages in one track may hold the same sort, and
 *    the order the picker shows is then whatever the planner returns.
 *
 * HOW THOSE THREE WERE ESTABLISHED
 * --------------------------------
 * Not by reading migration 048. Each was probed against the live table inside
 * one `begin … rollback`, in both directions, on 2026-09-04:
 *
 *   terminal + a cadence        REFUSED by lifecycle_stages_terminal_has_no_cadence
 *   non-terminal + NULL cadence ACCEPTED -- and a contact on that stage then
 *                               read effective_stale_after = 7
 *   a cadence of 0              ACCEPTED
 *   a duplicate (track, sort)   ACCEPTED
 *   delete of an IN-USE stage   REFUSED by contact_lifecycle_stage_fkey
 *   delete of an UNUSED stage   SUCCEEDED, with no is_active to retire it
 *   the legitimate 30 -> 45     WROTE
 *
 * The last line is the one that makes the rest evidence rather than an
 * outage: a table that refused everything would pass a suite of refusals.
 *
 * WHAT IS NOT WRITABLE FROM HERE, AND WHY
 * ---------------------------------------
 * `stage` and `track` are the primary key, and `contact_lifecycle_stage_fkey`
 * references them with NO ON UPDATE clause -- so renaming a stage in use is
 * refused outright, and renaming an unused one strands every future reference
 * to the old name. `is_terminal` decides whether anybody is chased at all.
 * None of the three is in StagePatch, and saveStage below has a test asserting
 * they never appear in a write.
 */

/**
 * Everything a saved cadence moves.
 *
 * THE CADENCE IS NOT STORED ANYWHERE. `contact_health` recomputes `due_at`
 * from `lifecycle_stages` on every read -- there is no materialized due date
 * and no trigger -- so one write to this table changes who is overdue across
 * the whole app at once. react-admin invalidates the resource that was
 * written and nothing else, so without this list the follow-up queue keeps
 * serving yesterday's overdue set from cache, with nothing on screen
 * suggesting the number is old. That is the same failure `invalidateTouched`
 * documents, arriving through a different door.
 *
 * `contact_lifecycle` IS DELIBERATELY ABSENT. This screen writes
 * lifecycle_stages only; a contact's own row is untouched by a cadence edit.
 * Naming it would be refetching a resource that provably cannot have moved,
 * and a list containing things the screen does not write is one nobody can
 * reason about the next time something IS missing from it.
 */
export const STAGE_SETTINGS_INVALIDATES = [
  "lifecycle_stages",
  "contact_health",
  "contacts_overdue",
  "contacts_stalled",
  "contacts_no_next_action",
] as const;

/** Ask react-query to refetch everything a stage edit changed. */
export function invalidateStageSettings(client: Invalidator): Promise<unknown> {
  return Promise.all(
    STAGE_SETTINGS_INVALIDATES.map((resource) =>
      client.invalidateQueries({ queryKey: [resource] }),
    ),
  );
}

/* ── the form ───────────────────────────────────────────────────────────── */

/**
 * One row's editable fields, as the strings an <input> actually holds.
 *
 * STRINGS, NOT NUMBERS. A numeric input's empty state is `""`, and coercing
 * that to a number gives 0 -- which is a legal write to this table and the
 * worst one available (see rule 2 in the header). Keeping the raw string as
 * far as the validator means "blank" and "zero" stay two different answers.
 */
export type StageDraft = {
  staleAfterDays: string;
  defaultNextAction: string;
  /** Empty string means "none". A select's empty option, not null. */
  taskType: string;
  /** The content asset id, as a string, for the same reason as the numbers. */
  contentId: string;
  sort: string;
};

/** What actually gets written. Five columns since 084, never more. */
export type StagePatch = {
  stale_after_days: number | null;
  default_next_action: string | null;
  default_task_type: string | null;
  default_content_id: number | null;
  sort: number;
};

export type DraftCheck = {
  /** Every problem, not the first -- so the form is fixed in one pass. */
  errors: string[];
  /** Null whenever `errors` is non-empty. */
  patch: StagePatch | null;
  /** Is this actually different from the row on screen? */
  changed: boolean;
};

/** Seed the form from the row. A null column is an empty box, not "null". */
export const emptyDraft = (stage: LifecycleStage): StageDraft => ({
  staleAfterDays:
    stage.stale_after_days == null ? "" : String(stage.stale_after_days),
  defaultNextAction: stage.default_next_action ?? "",
  taskType: stage.default_task_type ?? "",
  contentId:
    stage.default_content_id == null ? "" : String(stage.default_content_id),
  sort: String(stage.sort),
});

/** The largest value a Postgres `smallint` holds. Both columns are smallint. */
export const SMALLINT_MAX = 32767;

/**
 * A whole positive number, or null.
 *
 * `Number("")` is 0 and `Number(" ")` is 0, so parsing with Number() alone
 * turns an empty box into the one value that makes everybody permanently
 * overdue. Matching the digits first is the only reading that keeps blank and
 * zero apart.
 */
const wholeNumber = (raw: string): number | null => {
  const t = raw.trim();
  return /^\d+$/.test(t) ? Number(t) : null;
};

export const TRACK_LABEL: Record<LifecycleTrack, string> = {
  prospect: "Prospects",
  partner: "Partners",
};

/**
 * Check one row's edit, and produce the patch if it is safe.
 *
 * `all` is every stage on screen, needed only for the (track, sort) collision
 * check -- the database has no unique index to do it for us.
 */
export function checkStageDraft(
  stage: LifecycleStage,
  draft: StageDraft,
  all: LifecycleStage[],
): DraftCheck {
  const errors: string[] = [];

  /* ── cadence ── */
  const rawDays = draft.staleAfterDays.trim();
  let days: number | null = null;

  if (stage.is_terminal) {
    // The half the database DOES enforce. Naming the constraint means the
    // message on screen and the error PostgREST would return say the same
    // thing, so nobody has to guess which rule fired.
    if (rawDays !== "")
      errors.push(
        `A terminal stage has no cadence: there is nothing left to be late for. ` +
          `The database refuses it too — lifecycle_stages_terminal_has_no_cadence.`,
      );
  } else if (rawDays === "") {
    errors.push(
      `A cadence is required on a stage that is not terminal. The database ` +
        `would accept a blank one, but contact_health falls back to 7 — so ` +
        `clearing this does not switch the clock off, it moves everyone on ` +
        `${stage.track}/${stage.stage} onto a 7-day clock.`,
    );
  } else {
    days = wholeNumber(rawDays);
    if (days == null || days < 1)
      errors.push(
        `The cadence must be a whole number of days, at least 1. This table ` +
          `has no "greater than zero" check, so 0 would save — and then ` +
          `everyone on this stage is overdue for ever.`,
      );
    else if (days > SMALLINT_MAX)
      errors.push(
        `The cadence column is a smallint, so ${SMALLINT_MAX} is the ceiling.`,
      );
  }

  /* ── what the stage suggests doing next ── */
  //
  // THREE COLUMNS, ONE RULE. 084 refuses all of them on a terminal stage --
  // lifecycle_stages_terminal_suggests_nothing -- for the reason the cadence
  // is refused there too: there is nothing to do next on a stage whose whole
  // meaning is that there is nothing left to do. Naming the constraint means
  // the message here and the error PostgREST would return agree.
  const action = draft.defaultNextAction.trim();
  const rawType = draft.taskType.trim();
  const rawContent = draft.contentId.trim();

  if (
    stage.is_terminal &&
    (action !== "" || rawType !== "" || rawContent !== "")
  )
    errors.push(
      `A gate suggests nothing — the record is closed. The database refuses ` +
        `it too: lifecycle_stages_terminal_suggests_nothing.`,
    );

  // An empty box means "none", and the columns are nullable. Saving "" would
  // make a stage that LOOKS blank hold a value no reader treats as one.
  const nextAction = action === "" ? null : action;
  const taskType = rawType === "" ? null : rawType;

  let contentId: number | null = null;
  if (rawContent !== "") {
    contentId = wholeNumber(rawContent);
    if (contentId == null)
      errors.push(`“${rawContent}” is not a content asset id.`);
  }

  // A TEMPLATE WITHOUT A TYPE IS A SUGGESTION NOTHING CAN ACT ON. The task
  // the stage suggests needs to know what kind of work it is; a body with no
  // verb attached is the same shape of half-configuration as a cadence with
  // no stage.
  if (contentId != null && taskType == null)
    errors.push(
      `Choose a type as well. A template with no kind of work attached is a ` +
        `suggestion nothing can turn into a task.`,
    );

  /* ── sort ── */
  const sort = wholeNumber(draft.sort);
  if (sort == null || sort < 1)
    errors.push("The order must be a whole number, at least 1.");
  else if (sort > SMALLINT_MAX)
    errors.push(
      `The order column is a smallint, so ${SMALLINT_MAX} is the ceiling.`,
    );
  else {
    const clash = all.find(
      (s) =>
        s.track === stage.track &&
        !(s.stage === stage.stage && s.track === stage.track) &&
        s.sort === sort,
    );
    if (clash)
      errors.push(
        `${clash.track}/${clash.stage} already sits at ${sort}. There is no ` +
          `unique index on (track, sort), so two stages sharing a number is a ` +
          `list that reorders itself between page loads.`,
      );
  }

  const changed =
    (stage.stale_after_days ?? null) !== days ||
    (stage.default_next_action ?? null) !== nextAction ||
    (stage.default_task_type ?? null) !== taskType ||
    (stage.default_content_id ?? null) !== contentId ||
    stage.sort !== sort;

  if (errors.length) return { errors, patch: null, changed };

  return {
    errors,
    changed,
    patch: {
      stale_after_days: days,
      default_next_action: nextAction,
      default_task_type: taskType,
      default_content_id: contentId,
      sort: sort as number,
    },
  };
}

/* ── the impact preview ─────────────────────────────────────────────────── */

/**
 * What a cadence change would do to the people already on the stage.
 *
 * WHY THIS EXISTS. `due_at` is computed live, so editing 30 to 45 does not
 * apply "from now on" -- it retroactively restates when every partner was
 * due, including the ones already overdue. Thirty-three contacts sit on
 * prospect/contacted today. Nobody should move thirty-three follow-up dates
 * without being told the number first.
 */
export type CadenceImpact = {
  /** Everybody whose contact_lifecycle row names this stage. */
  onStage: number;
  /** Of those, the ones this edit can actually move. */
  following: number;
  /** Excluded: they carry their own cadence, which beats the stage default. */
  overridden: number;
  /** Excluded: a pause outranks the clock until it expires. */
  paused: number;
  /** How many of `following` the view calls stale today. */
  overdueNow: number;
  /** How many of `following` would be stale on the new cadence. */
  overdueAfter: number;
  startChasing: number;
  stopChasing: number;
};

const DAY_MS = 86_400_000;

/** The later of two instants, treating null as "no opinion". */
const later = (a: number | null, b: number | null): number | null =>
  a == null ? b : b == null ? a : Math.max(a, b);

const at = (iso: string | null): number | null =>
  iso == null ? null : new Date(iso).getTime();

/**
 * Recompute `due_at` the way migration 062's view does, for one contact, on a
 * proposed cadence.
 *
 * THE VIEW'S OWN EXPRESSION IS
 *     GREATEST(clock_start + days, commitment_at, paused_until + 1)
 * and this reproduces the first two terms only. The third is provably
 * irrelevant here because paused contacts are excluded by the caller: for a
 * contact who is NOT paused, `paused_until + 1` is already in the past, so it
 * can only win the GREATEST when `clock_start + days` is in the past too --
 * in which case the contact is overdue on either reading. A fake that derives
 * differently is a fake that lies, so the one omission gets a proof.
 *
 * A null result means null due_at, which the view reads as stale: a contact
 * with no last touch and no first_seen is exactly the population this system
 * exists to catch, and no cadence may quieten them.
 */
const dueAtOn = (row: ContactHealth, days: number): number | null => {
  const clock = at(row.clock_start);
  return later(
    clock == null ? null : clock + days * DAY_MS,
    at(row.commitment_at),
  );
};

export function cadenceImpact(
  rows: ContactHealth[],
  stage: LifecycleStage,
  newDays: number,
  now: Date,
): CadenceImpact {
  const stageDefault = stage.stale_after_days ?? 7;

  /*
   * WHO ACTUALLY FOLLOWS THIS STAGE. effective_stale_after is
   * coalesce(cl.stale_after_days, s.stale_after_days, 7), so a row whose
   * effective value disagrees with the stage default is sitting on a
   * per-contact override and this edit cannot move it. Counting it would
   * overstate the blast radius, which is the one number this preview exists
   * to get right.
   */
  const overridden = rows.filter(
    (r) => !r.is_paused && r.effective_stale_after !== stageDefault,
  );
  const paused = rows.filter((r) => r.is_paused);
  const following = rows.filter(
    (r) => !r.is_paused && r.effective_stale_after === stageDefault,
  );

  const t = now.getTime();
  let overdueAfter = 0;
  let startChasing = 0;
  let stopChasing = 0;

  for (const row of following) {
    const due = dueAtOn(row, newDays);
    const willBeOverdue = due == null || t > due;
    if (willBeOverdue) overdueAfter += 1;
    // TODAY'S ANSWER COMES FROM THE VIEW, not from re-deriving it here. The
    // view is what the three accountability queues select on, so if our
    // arithmetic ever disagreed with it, the view would still be right.
    if (!row.is_stale && willBeOverdue) startChasing += 1;
    if (row.is_stale && !willBeOverdue) stopChasing += 1;
  }

  return {
    onStage: rows.length,
    following: following.length,
    overridden: overridden.length,
    paused: paused.length,
    overdueNow: following.filter((r) => r.is_stale).length,
    overdueAfter,
    startChasing,
    stopChasing,
  };
}

/* ── grouping ───────────────────────────────────────────────────────────── */

export type TrackGroup = { track: LifecycleTrack; stages: LifecycleStage[] };

/**
 * The stages, by track, in the order the pickers show them.
 *
 * PROSPECTS FIRST because that is where a contact starts and where eleven of
 * the twelve rows' traffic is. A track with no rows is dropped rather than
 * rendered as an empty heading -- `lifecycle_stages_track_check` allows only
 * these two values, so a third can never appear and no fallback bucket is
 * needed.
 *
 * TIES BREAK ON THE STAGE NAME. A duplicate sort is a state this screen
 * refuses to create but that a hand-run UPDATE can, and an unstable order
 * would then make the list reshuffle between renders with nothing to blame.
 */
export function groupByTrack(stages: LifecycleStage[]): TrackGroup[] {
  const order: LifecycleTrack[] = ["prospect", "partner"];
  return order
    .map((track) => ({
      track,
      stages: stages
        .filter((s) => s.track === track)
        .sort((a, b) => a.sort - b.sort || a.stage.localeCompare(b.stage)),
    }))
    .filter((g) => g.stages.length > 0);
}

/* ── the write ──────────────────────────────────────────────────────────── */

/**
 * Save one stage.
 *
 * A PLAIN data-provider update, which is why this is three lines: the table
 * has a real primary key declared in NON_ID_PRIMARY_KEYS, so ra-data-postgrest
 * mints the virtual `id` on the way in and strips it on the way out, and
 * PostgREST gets `?stage=eq.active&track=eq.partner` rather than the `?id=eq.`
 * it would default to against a table with no such column.
 *
 * `id` IS PASSED STRAIGHT BACK FROM THE ROW WE READ. Nothing here builds or
 * parses the compound-key encoding: that shape belongs to the provider, and a
 * second implementation of it in a screen is a second thing to get wrong the
 * day it changes.
 *
 * The invalidation is NOT done here. It needs react-query's client, which is a
 * React concern; keeping it out means this function is provable with a two-line
 * fake and no browser -- see ./lifecycleStageRules.test.ts.
 */
export async function saveStage(
  dataProvider: DataProvider,
  stage: LifecycleStage,
  patch: StagePatch,
): Promise<void> {
  await dataProvider.update("lifecycle_stages", {
    id: stage.id,
    data: patch,
    previousData: stage,
  });
}

/* ── adding and removing a stage (084) ─────────────────────────────────── */

export type NewStageDraft = {
  /** What a person types. Becomes the primary key with the track. */
  name: string;
  /** Blank on a gate: a terminal stage has no cadence, and 084 refuses one. */
  cadenceDays: string;
  isTerminal: boolean;
};

export type NewStageCheck =
  | { ok: false; errors: string[] }
  | { ok: true; errors: []; row: NewStageRow };

export type NewStageRow = {
  track: string;
  stage: string;
  sort: number;
  stale_after_days: number | null;
  is_terminal: boolean;
};

/**
 * A stage key: lowercase, letters, digits and single spaces collapsed to one.
 *
 * The name IS the key -- `lifecycle_stages` has no separate label column, and
 * `contact_lifecycle.lifecycle_stage` stores the string. So it is normalized
 * once at creation and never rewritten: the foreign key has no ON UPDATE
 * clause, and renaming a stage in use would be refused with contacts on it and
 * would silently strand them if there were none.
 */
export function stageKeyOf(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export function checkNewStage(
  draft: NewStageDraft,
  track: string,
  all: readonly LifecycleStage[],
): NewStageCheck {
  const errors: string[] = [];
  const key = stageKeyOf(draft.name);

  if (key === "") errors.push("A stage needs a name.");
  else if (all.some((s) => s.track === track && s.stage === key))
    errors.push(
      `${track} already has a stage called “${key}”. The pair (stage, track) ` +
        `is the primary key.`,
    );

  const raw = draft.cadenceDays.trim();
  let days: number | null = null;

  if (draft.isTerminal) {
    if (raw !== "")
      errors.push(
        `A gate has no cadence — there is nothing left to be late for. The ` +
          `database refuses it too: lifecycle_stages_terminal_has_no_cadence.`,
      );
  } else if (raw === "") {
    errors.push(
      `Give it a cadence. Leaving it blank does not switch the clock off — ` +
        `contact_health falls back to 7 days for everyone on the stage.`,
    );
  } else {
    days = wholeNumber(raw);
    if (days == null || days < 1)
      errors.push(
        `The cadence must be a whole number of days, at least 1. Zero would ` +
          `save, and then everyone on this stage is overdue for ever.`,
      );
    else if (days > SMALLINT_MAX)
      errors.push(
        `The cadence column is a smallint, so ${SMALLINT_MAX} is the ceiling.`,
      );
  }

  if (errors.length > 0) return { ok: false, errors };

  /*
    GATES SORT AFTER EVERYTHING, and the number is chosen rather than typed.
    A gate's position carries no meaning -- it is an ending, and endings are
    at the end -- so the editor does not offer the field at all. Starting them
    at GATE_SORT_BASE keeps a hand-run `order by sort` reading the way the
    screen does, which orders by (is_terminal, sort) regardless.
  */
  const peers = all.filter(
    (s) => s.track === track && s.is_terminal === draft.isTerminal,
  );
  const base = draft.isTerminal ? GATE_SORT_BASE : 0;
  const sort = Math.max(base, ...peers.map((s) => s.sort)) + 1;

  return {
    ok: true,
    errors: [],
    row: {
      track,
      stage: key,
      sort,
      stale_after_days: days,
      is_terminal: draft.isTerminal,
    },
  };
}

/**
 * May this stage be removed?
 *
 * THE DATABASE REFUSES IT ANYWAY. `contact_lifecycle_stage_fkey` references
 * (stage, track) with no ON DELETE clause, so a stage in use cannot be
 * deleted at all. This exists so the screen can say why, with the count, and
 * before somebody presses a button and reads a constraint name.
 *
 * A TRACK MUST KEEP AT LEAST ONE GATE. Removing the last one leaves every
 * contact on the track with no permanent exit -- the trap lifecycleTrackRules
 * exists to prevent at creation, reachable here by deletion instead.
 */
export function stageRemovalVerdict(
  stage: LifecycleStage,
  contactsOnStage: number,
  all: readonly LifecycleStage[],
): { ok: true } | { ok: false; reason: string } {
  if (contactsOnStage > 0)
    return {
      ok: false,
      reason:
        `${contactsOnStage} ${contactsOnStage === 1 ? "contact is" : "contacts are"} ` +
        `on this stage. Move them first — the database refuses the delete ` +
        `rather than cascading, because cascading would unstage them.`,
    };

  if (stage.is_terminal) {
    const gates = all.filter((s) => s.track === stage.track && s.is_terminal);
    if (gates.length <= 1)
      return {
        ok: false,
        reason:
          `This is the only gate on ${stage.track}. Without one, nobody on ` +
          `this track can ever be closed — they are chased for ever and ` +
          `nothing reports it. Add another gate first.`,
      };
  }

  return { ok: true };
}

export async function createStage(
  dataProvider: DataProvider,
  row: NewStageRow,
): Promise<void> {
  await dataProvider.create("lifecycle_stages", { data: row });
}

export async function deleteStage(
  dataProvider: DataProvider,
  stage: LifecycleStage,
): Promise<void> {
  await dataProvider.delete("lifecycle_stages", {
    id: stage.id,
    previousData: stage,
  });
}

/* ── which templates suit which kind of work ───────────────────────────── */

/**
 * The content kind each task type is written in, where one exists.
 *
 * THIS IS 075'S MAPPING, READ BACKWARDS, AND IT IS DELIBERATELY SHORT.
 * `private.enrollment_step_task_type` derives a task type from an asset kind
 * with exactly two cases -- email -> email, call_script -> call -- and its
 * error message says why the rest are missing: "linkedin, note and other are
 * permitted content kinds with no clean counterpart in taskTypes, and 066
 * exists because a plausible-looking task type was once assumed rather than
 * checked."
 *
 * So a demo, a lunch or a meeting has NO corresponding kind, and inventing
 * one here would be that same assumption in a new place.
 */
export const KIND_FOR_TASK_TYPE: Record<string, string> = {
  email: "email",
  call: "call_script",
};

export type TemplateChoices<T> = {
  /** What to offer. */
  assets: T[];
  /** Whether the list was narrowed, so the screen can say so. */
  filtered: boolean;
  /** The kind it was narrowed to, or null when nothing suits. */
  kind: string | null;
  /** Why the list is empty, when it is. Null when there is nothing to say. */
  reason: string | null;
};

/**
 * The templates worth offering for a kind of work.
 *
 * A TYPE WITH NO CORRESPONDING KIND IS OFFERED NOTHING, and the first version
 * of this offered everything -- which was wrong twice over.
 *
 * It contradicted a rule already in this file. checkStageDraft REFUSES to save
 * a template with no type beside it: "a suggestion nothing can turn into a
 * task". Offering the whole library for a stage with no type set was inviting
 * a choice the save would then reject.
 *
 * And it was wrong about the subject. A template is the words of a MESSAGE.
 * Ship, Lunch and Demo are not messages, so no email template belongs to a
 * shipping task -- showing all of them is not "help, unfiltered", it is a list
 * whose every entry is wrong.
 *
 * THE REASON IS RETURNED rather than left as an empty select, because a picker
 * with nothing in it and no explanation reads as a library that failed to
 * load.
 *
 * A TEMPLATE ALREADY CHOSEN IS STILL OFFERED, whatever the type. Otherwise
 * changing the type would drop the selection to the empty option and the next
 * save would clear the column without saying so.
 */
export function templatesFor<T extends { id: number; kind: string }>(
  assets: readonly T[],
  taskType: string | null,
  chosenId: number | null,
): TemplateChoices<T> {
  const keepChosen = (a: T) => a.id === chosenId;

  if (!taskType)
    return {
      assets: assets.filter(keepChosen),
      filtered: true,
      kind: null,
      reason: "Choose a type first — a template is the words of a message.",
    };

  const kind = KIND_FOR_TASK_TYPE[taskType] ?? null;
  if (kind === null)
    return {
      assets: assets.filter(keepChosen),
      filtered: true,
      kind: null,
      reason:
        `Nothing in the library is written for a ${taskType}. Templates are ` +
        `email copy and call scripts; 075 maps those two and nothing else.`,
    };

  return {
    assets: assets.filter((a) => a.kind === kind || keepChosen(a)),
    filtered: true,
    kind,
    reason: null,
  };
}
